import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AgentDecision } from '../../lib/agentDecisions'
import { decisionTitle, expiryLabel, sortAgentDecisions } from '../../lib/agentDecisions'

type AgentSuggestionCardProps = {
  decisions: AgentDecision[]
  onAccept: (decisionId: string) => Promise<void>
  onSkip: (decisionId: string) => Promise<void>
  onTellMeMore: (decisionId: string) => void
}

const AgentSuggestionCard = ({
  decisions,
  onAccept,
  onSkip,
  onTellMeMore,
}: AgentSuggestionCardProps) => {
  const { t } = useTranslation()
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const sorted = useMemo(() => sortAgentDecisions(decisions), [decisions])
  const primary = sorted[0]

  if (!primary) return null

  const title = decisionTitle(primary.action_type, primary.action_payload)
  const expiry = expiryLabel(primary.expires_at)
  const moreCount = sorted.length - 1

  const runAccept = async () => {
    setActionError(null)
    setLoadingId(primary.id)
    try {
      await onAccept(primary.id)
    } catch {
      setActionError(t('coach.agent.acceptFailed'))
    } finally {
      setLoadingId(null)
    }
  }

  const runSkip = async () => {
    setActionError(null)
    setLoadingId(primary.id)
    try {
      await onSkip(primary.id)
    } catch {
      setActionError(t('coach.agent.acceptFailed'))
    } finally {
      setLoadingId(null)
    }
  }

  const expiryClass =
    expiry.tone === 'danger'
      ? 'text-red-400'
      : expiry.tone === 'warn'
        ? 'text-amber-400'
        : 'text-kilo-soft'

  return (
    <div className="rounded-xl border border-white/10 bg-[#1A1A1A] p-4 shadow-kilo-card">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#00C853]" aria-hidden />
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00C853]">
            {t('coach.agent.recommends')}
          </p>
        </div>
        {expiry.text ? (
          <span className={`text-[10px] font-medium uppercase tracking-wide ${expiryClass}`}>{expiry.text}</span>
        ) : null}
      </div>

      <h3 className="text-base font-semibold leading-snug text-kilo-text">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-kilo-muted">{primary.reasoning}</p>

      {actionError ? <p className="mt-3 text-sm text-red-400">{actionError}</p> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={loadingId === primary.id}
          onClick={() => void runAccept()}
          className="rounded-lg bg-[#00C853] px-4 py-2 text-sm font-semibold text-black transition-opacity disabled:opacity-50"
        >
          {t('coach.agent.accept')}
        </button>
        <button
          type="button"
          disabled={loadingId === primary.id}
          onClick={() => void runSkip()}
          className="rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-kilo-text transition-colors hover:border-white/20 disabled:opacity-50"
        >
          {t('coach.agent.skipToday')}
        </button>
        <button
          type="button"
          onClick={() => onTellMeMore(primary.id)}
          className="px-2 py-2 text-sm font-medium text-kilo-accent transition-colors hover:text-kilo-text"
        >
          {t('coach.agent.tellMeMore')}
        </button>
      </div>

      {moreCount > 0 ? (
        <button
          type="button"
          onClick={() => onTellMeMore(sorted[1]?.id ?? primary.id)}
          className="mt-3 text-xs text-kilo-soft transition-colors hover:text-kilo-text"
        >
          {t('coach.agent.moreRecommendations', { count: moreCount })}
        </button>
      ) : null}
    </div>
  )
}

export default AgentSuggestionCard
